import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, axiosInstance } from '../../App';

// Plan labels — '6months' kept for legacy subscribers whose dashboards must
// still render their plan name correctly even though new signups can no
// longer choose it (Iter 113 reset).
export const PLAN_LABELS = {
  'free': 'Free Directory',
  'basic': 'Basic',
  'basic_monthly': 'Basic',
  'plus_monthly': 'Business Plus',
  'plus_annual': 'Business Plus (Annual)',
  '6months': '6-Month Plan (legacy)',
  '12months': 'Best Setup / Full Access',
  'founders': 'Founding Member · Full Access'
};

export const PLAN_AMOUNTS = {
  'free': '\u20AC0 · Free forever',
  'basic': 'Founding Business Offer · FREE for 12 months',
  'basic_monthly': '\u20AC1.99 / month',
  'plus_monthly': '\u20AC2.99 / month',
  'plus_annual': '\u20AC29.99 / year',
  '6months': '\u20AC49 / 6 months (legacy)',
  '12months': 'Founding Business Offer · FREE for 12 months',
  'founders': 'Founding Business Offer · FREE for 12 months'
};

// Feature matrix for basic plan gating (mirrors backend config.plan_allows)
export const PLAN_FEATURES = {
  'basic':    { events: false, offers: false, amenities: false, boost: false },
  '6months':  { events: true,  offers: true,  amenities: true,  boost: true  },
  '12months': { events: true,  offers: true,  amenities: true,  boost: true  },
  // Founding Business Offer (free for 12 months) — full access, mirrors
  // backend config.SUBSCRIPTION_PLANS['founders']. Without this entry the UI
  // fell back to Basic and wrongly blocked founders from posting events.
  'founders': { events: true,  offers: true,  amenities: true,  boost: true  },
};

export const planAllows = (planId, feature, paid = false) => {
  const plan = PLAN_FEATURES[planId];
  if (plan) return !!plan[feature];
  // unknown/legacy/seed plan — mirror backend config.plan_allows:
  if (paid) return !!(PLAN_FEATURES['basic'] || {})[feature]; // paid + unknown → lock to Basic
  return true; // unpaid/seed/community → allow (server still enforces)
};

// Founding-Free model (mirrors backend config.effective_plan + moderation):
// every APPROVED business is free for 12 months with full access. We normalise
// the business record once on load so the whole dashboard (status banner,
// subscription panel, event/offer entitlement) renders the correct free state
// even if the stored subscriptionPlan/subscriptionStatus were never migrated.
// Genuine active/lapsed PAID subscribers keep their real state untouched.
const _PAID_PLAN_IDS = ['basic', '6months', '12months', 'basic_monthly', 'plus_monthly', 'plus_annual'];
export const normalizeFoundingFree = (b) => {
  if (!b) return b;
  if (b.subscriptionPlan === 'free') return b;                          // Free Directory — keep as-is
  if (_PAID_PLAN_IDS.includes(b.subscriptionPlan)) return b;            // real paid plan (incl. new monthly/annual + trials)
  if (['cancelled', 'expired'].includes(b.subscriptionStatus)) return b; // lapsed — keep honest
  if (b.approvalStatus !== 'approved') return b;                        // not yet approved
  let end = b.subscriptionEnd;
  if (!end) {
    const start = b.approvedAt || b.createdAt;
    if (start) {
      const d = new Date(start);
      if (!isNaN(d.getTime())) { d.setDate(d.getDate() + 365); end = d.toISOString(); }
    }
  }
  return {
    ...b,
    subscriptionPlan: 'founders',
    subscriptionStatus: 'active',
    paymentConfirmed: true,
    subscriptionEnd: end || b.subscriptionEnd,
  };
};

// Mirror of backend free-tier / Featured limits (config.py + services/featured.py)
export const FREE_MAX_ACTIVE_EVENTS = 5;
export const FEATURED_MAX_ACTIVE_EVENTS = 15;

export const isFeaturedBusiness = (b) => {
  const status = (b?.featuredStatus || '').toLowerCase();
  if (!['active', 'trialing'].includes(status)) return false;
  const exp = b?.featuredExpiresAt;
  if (!exp) return true;
  const d = new Date(exp);
  return isNaN(d.getTime()) ? true : d.getTime() > Date.now();
};

// Pre-flight eligibility so we can warn + block the create path BEFORE the
// business fills the whole form. Mirrors the guards in POST /api/business/event.
export const getEventEligibility = (business, myEvents = []) => {
  if (!business) return { allowed: true, reason: null };
  if (business.subscriptionStatus === 'paused') {
    return { allowed: false, reason: 'trialEnded' };
  }
  const paid = !!business.paymentConfirmed || business.subscriptionStatus === 'trial';
  if (!planAllows(business.subscriptionPlan, 'events', paid)) {
    return { allowed: false, reason: 'planNoEvents' };
  }
  const max = isFeaturedBusiness(business) ? FEATURED_MAX_ACTIVE_EVENTS : FREE_MAX_ACTIVE_EVENTS;
  // Active = pending/approved AND not yet past its end date. Mirrors the
  // backend count (and the "Active" tab) so expired events never block a slot.
  const now = Date.now();
  const activeCount = (myEvents || []).filter(e =>
    ['pending', 'approved'].includes(e.status) &&
    (e.endDate ? new Date(e.endDate).getTime() >= now : true)
  ).length;
  if (activeCount >= max) {
    return { allowed: false, reason: 'maxEvents', max };
  }
  return { allowed: true, reason: null };
};

// Normalise an axios error into a human-readable string. FastAPI 422 responses
// put `detail` as an array of objects, which previously rendered as an empty /
// "[object Object]" toast — making event-save failures look silent.
export const extractEventError = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || fallback;
  return fallback;
};

export const EVENT_CATEGORIES = [
  { value: 'music', key: 'categoryMusic' },
  { value: 'sports', key: 'categorySports' },
  { value: 'food', key: 'categoryFood' },
  { value: 'arts', key: 'categoryArts' },
  { value: 'networking', key: 'categoryNetworking' },
  { value: 'community', key: 'categoryCommunity' },
  { value: 'workshop', key: 'categoryWorkshop' },
  { value: 'market', key: 'categoryMarket' },
  { value: 'festival', key: 'categoryFestival' },
  { value: 'charity', key: 'categoryCharity' },
  { value: 'other', key: 'categoryOther' },
];

export default function useBusinessDashboard(t) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const fileInputRef = useRef(null);
  const posterInputRef = useRef(null);

  // Core state
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [retrying, setRetrying] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all');

  // Events state
  const [myEvents, setMyEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [posterUploading, setPosterUploading] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', category: '', customCategory: '', date: '', startTime: '', endTime: '',
    price: '', location: '', image: null, notifyFollowers: true, boostPost: false
  });
  const [eventErrors, setEventErrors] = useState({});
  const [editingEventId, setEditingEventId] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);

  // Offers state
  const [myOffers, setMyOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [offerForm, setOfferForm] = useState({
    title: '', description: '', discountPercentage: '', validUntil: '', image: null, notifyFollowers: true, boostPost: false
  });
  const [offerErrors, setOfferErrors] = useState({});
  const [offerImageUploading, setOfferImageUploading] = useState(false);
  const offerImageInputRef = useRef(null);

  // Boost state
  const [boostTiers, setBoostTiers] = useState([]);
  const [boostModal, setBoostModal] = useState(null);
  const [selectedTier, setSelectedTier] = useState('24h');
  const [boostLoading, setBoostLoading] = useState(false);
  const [boostHistory, setBoostHistory] = useState([]);
  const [boostHistoryLoading, setBoostHistoryLoading] = useState(false);

  // Portal state
  const [portalLoading, setPortalLoading] = useState(false);

  // Temporary closures / business status
  const [closures, setClosures] = useState([]);
  const [closuresLoading, setClosuresLoading] = useState(false);
  const [closureSubmitting, setClosureSubmitting] = useState(false);

  const fetchClosures = async () => {
    try {
      setClosuresLoading(true);
      const res = await axiosInstance.get('/business/me/closures');
      setClosures(res.data.closures || []);
    } catch { /* non-fatal */ } finally {
      setClosuresLoading(false);
    }
  };

  const createClosure = async (form) => {
    setClosureSubmitting(true);
    try {
      await axiosInstance.post('/business/me/closures', form);
      setMessage({ type: 'success', text: 'Business status saved.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await fetchClosures();
      return true;
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Could not save status' });
      return false;
    } finally {
      setClosureSubmitting(false);
    }
  };

  const deleteClosure = async (id) => {
    try {
      await axiosInstance.delete(`/business/me/closures/${id}`);
      await fetchClosures();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Could not remove closure' });
    }
  };

  const updateClosure = async (id, form) => {
    setClosureSubmitting(true);
    try {
      await axiosInstance.put(`/business/me/closures/${id}`, form);
      setMessage({ type: 'success', text: 'Business status updated.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await fetchClosures();
      return true;
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Could not update status' });
      return false;
    } finally {
      setClosureSubmitting(false);
    }
  };

  const reopenClosure = async (id) => {
    try {
      await axiosInstance.post(`/business/me/closures/${id}/reopen`);
      setMessage({ type: 'success', text: 'Marked as reopened.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await fetchClosures();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Could not reopen' });
    }
  };

  // ============= FETCH =============
  const fetchBusiness = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/business/me');
      setBusiness(normalizeFoundingFree(response.data));
      setEditForm({
        businessName: response.data.businessName,
        description: response.data.description,
        category: response.data.category || '',
        address: response.data.address || '',
        city: response.data.city || '',
        country: response.data.country || '',
        countryCode: response.data.countryCode || '',
        region: response.data.region || '',
        province: response.data.province || '',
        localArea: response.data.localArea || '',
        postcode: response.data.postcode || '',
        email: response.data.email || '',
        phone: response.data.phone,
        whatsapp: response.data.whatsapp || '',
        website: response.data.website || '',
        socialLinks: response.data.socialLinks || {},
        hours: response.data.hours || {},
        specialHours: response.data.specialHours || '',
        groupSizes: response.data.groupSizes || [],
        dietaryOptions: response.data.dietaryOptions || [],
        eventTypes: response.data.eventTypes || [],
        venueType: response.data.venueType || [],
        additionalFeatures: response.data.additionalFeatures || [],
        accessibilityFeatures: response.data.accessibilityFeatures || [],
      });
    } catch (error) {
      console.error('Error fetching business:', error);
      setMessage({ type: 'error', text: 'Failed to load business profile' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (period) => {
    try {
      const p = period || analyticsPeriod;
      const res = await axiosInstance.get(`/business/me/analytics?period=${p}`);
      setAnalytics(res.data);
    } catch (err) {
      console.error('Analytics error:', err);
    }
  };

  const changeAnalyticsPeriod = (period) => {
    setAnalyticsPeriod(period);
    fetchAnalytics(period);
  };

  const fetchMyEvents = async () => {
    try {
      setEventsLoading(true);
      // Fetch ALL (active + archived + expired) so the dashboard tabs can split
      // them client-side and show counts. Default server view is `active`, so
      // we explicitly request `all` here.
      const res = await axiosInstance.get('/business/events?view=all');
      setMyEvents(res.data);
    } catch (err) {
      console.error('Events error:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  const restoreEvent = async (eventId) => {
    try {
      await axiosInstance.post(`/business/event/${eventId}/restore`);
      await fetchMyEvents();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || err.message };
    }
  };

  const fetchMyOffers = async () => {
    try {
      setOffersLoading(true);
      const res = await axiosInstance.get('/business/offers?view=all');
      setMyOffers(res.data);
    } catch (err) {
      console.error('Offers error:', err);
    } finally {
      setOffersLoading(false);
    }
  };

  const restoreOffer = async (offerId) => {
    try {
      await axiosInstance.post(`/business/offer/${offerId}/restore`);
      await fetchMyOffers();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || err.message };
    }
  };

  const fetchBoostTiers = async () => {
    try {
      const res = await axiosInstance.get('/stripe/boost-tiers');
      setBoostTiers(res.data.tiers || []);
    } catch {
      console.error('Failed to fetch boost tiers');
    }
  };

  const fetchBoostHistory = async () => {
    try {
      setBoostHistoryLoading(true);
      const res = await axiosInstance.get('/business/boost-history');
      setBoostHistory(res.data);
    } catch { }
    finally { setBoostHistoryLoading(false); }
  };

  // ============= INIT =============
  useEffect(() => {
    if (user?.role !== 'business') {
      navigate('/signin');
      return;
    }
    fetchBusiness();
    fetchAnalytics();
    fetchMyEvents();
    fetchMyOffers();
    fetchBoostTiers();
    fetchBoostHistory();
    fetchClosures();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Iter 182 — Deep-link support for owner inline controls on
  // BusinessDetail.jsx. Recognises `?edit=event:{id}`, `?edit=offer:{id}`,
  // `?duplicate=event:{id}` and `?duplicate=offer:{id}` once the relevant
  // collection has finished loading, then scrolls the matching section into
  // view. The param is stripped from the URL after handling so a refresh
  // doesn't re-trigger.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const editParam = params.get('edit');
    const duplicateParam = params.get('duplicate');
    const raw = editParam || duplicateParam;
    if (!raw) return;
    const [kind, targetId] = raw.split(':');
    if (!kind || !targetId) return;

    const handle = (collection, opener, sectionId) => {
      const item = collection.find((x) => x.id === targetId);
      if (!item) return false;
      opener(item);
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      params.delete('edit');
      params.delete('duplicate');
      const next = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${next ? '?' + next : ''}`);
      return true;
    };

    if (kind === 'event' && myEvents.length) {
      const opener = editParam ? openEditForm : handleDuplicateEvent;
      handle(myEvents, opener, 'events-section');
    } else if (kind === 'offer' && myOffers.length) {
      const opener = editParam ? openEditOffer : handleDuplicateOffer;
      handle(myOffers, opener, 'offers-section');
    }
  // openEditForm/openEditOffer etc are stable closures defined below; rerunning
  // when the collections change is exactly what we want.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEvents, myOffers]);

  // ============= ACTIONS =============
  const showMsg = (type, text, duration = 5000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), duration);
  };

  const handleRetryPayment = async () => {
    setRetrying(true);
    try {
      const originUrl = window.location.origin;
      const response = await axiosInstance.post('/stripe/retry-checkout', { originUrl });
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      console.error('Retry payment error:', error);
      showMsg('error', error.response?.data?.detail || 'Failed to create payment session', 4000);
      setRetrying(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showMsg('error', 'Please select an image file', 3000); return; }
    if (file.size > 5 * 1024 * 1024) { showMsg('error', 'Image must be less than 5MB', 3000); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axiosInstance.post('/business/upload-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg('success', 'Photo uploaded successfully!', 3000);
      fetchBusiness();
    } catch (error) {
      showMsg('error', error.response?.data?.detail || 'Failed to upload photo', 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showMsg('error', 'Please select an image file', 3000); return; }
    if (file.size > 5 * 1024 * 1024) { showMsg('error', 'Image must be less than 5MB', 3000); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axiosInstance.post('/business/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg('success', 'Logo updated!', 3000);
      fetchBusiness();
    } catch (error) {
      showMsg('error', error.response?.data?.detail || 'Failed to upload logo', 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axiosInstance.delete('/business/photo', { params: { photo_url: photoUrl } });
      showMsg('success', 'Photo deleted', 3000);
      fetchBusiness();
    } catch (error) {
      showMsg('error', 'Failed to delete photo', 3000);
    }
  };

  const handleReorderPhotos = async (newOrder) => {
    try {
      await axiosInstance.put('/business/photos/reorder', { photos: newOrder });
      fetchBusiness();
    } catch (error) {
      showMsg('error', 'Failed to reorder photos', 3000);
    }
  };

  const handleSaveEdit = async () => {
    try {
      // Build final payload, merging "Other" custom texts into arrays
      const payload = { ...editForm };

      // Handle eventTypes "Other" — replace with custom text
      if (payload.eventTypes?.includes('Other') && payload.customEventType?.trim()) {
        payload.eventTypes = payload.eventTypes.filter(e => e !== 'Other');
        payload.eventTypes.push(payload.customEventType.trim());
      } else if (payload.eventTypes) {
        payload.eventTypes = payload.eventTypes.filter(e => e !== 'Other');
      }
      delete payload.customEventType;

      // Handle additionalFeatures "Other" — custom text is already appended by handleCustomFeatureChange
      // Just strip the "Other" marker before sending
      if (payload.additionalFeatures) {
        payload.additionalFeatures = payload.additionalFeatures.filter(f => f !== 'Other');
      }

      // EmailStr rejects empty string — only send a contact email when set.
      if (!payload.email || !payload.email.trim()) delete payload.email;

      await axiosInstance.put('/business/me', payload);
      showMsg('success', 'Profile updated!', 3000);
      setEditing(false);
      fetchBusiness();
    } catch (error) {
      showMsg('error', 'Failed to update profile', 3000);
    }
  };

  // ============= EVENTS =============
  const validateEventForm = () => {
    const errors = {};
    if (!eventForm.title.trim()) errors.title = t('eventTitleRequired');
    if (!eventForm.date) errors.date = t('eventDateRequired');
    if (!eventForm.startTime) errors.startTime = t('eventStartTimeRequired');
    if (!eventForm.endTime) errors.endTime = t('eventEndTimeRequired');
    if (!eventForm.category) errors.category = t('eventCategoryRequired');
    if (eventForm.category === 'other' && !eventForm.customCategory.trim()) errors.customCategory = t('customCategoryRequired', 'Please describe the event type');
    if (!eventForm.location.trim()) errors.location = t('eventVenueRequired');
    if (eventForm.startTime && eventForm.endTime && eventForm.startTime >= eventForm.endTime) {
      errors.endTime = t('eventEndAfterStart');
    }
    setEventErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePosterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosInstance.post('/business/event/upload-poster', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEventForm(prev => ({ ...prev, image: res.data.url }));
    } catch (err) {
      showMsg('error', 'Failed to upload poster');
    } finally {
      setPosterUploading(false);
      if (posterInputRef.current) posterInputRef.current.value = '';
    }
  };

  // Iter 184 — Offer image upload (single image, parity with event poster).
  // Multi-photo galleries / paid extras are deliberately deferred.
  const handleOfferImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOfferImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosInstance.post('/business/offer/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOfferForm(prev => ({ ...prev, image: res.data.url }));
    } catch (err) {
      showMsg('error', err?.response?.data?.detail || 'Failed to upload offer image');
    } finally {
      setOfferImageUploading(false);
      if (offerImageInputRef.current) offerImageInputRef.current.value = '';
    }
  };

  const handleEventSubmit = async () => {
    if (!validateEventForm()) return;
    setEventSubmitting(true);
    try {
      const startDate = `${eventForm.date}T${eventForm.startTime}:00`;
      const endDate = `${eventForm.date}T${eventForm.endTime}:00`;
      const finalCategory = eventForm.category === 'other' && eventForm.customCategory.trim()
        ? `other: ${eventForm.customCategory.trim()}`
        : eventForm.category;
      const payload = {
        title: eventForm.title.trim(), description: eventForm.description.trim(),
        category: finalCategory, startDate, endDate,
        location: eventForm.location.trim(),
        price: eventForm.price ? parseFloat(eventForm.price) : null,
        image: eventForm.image || null,
        notifyFollowers: eventForm.notifyFollowers, boostPost: eventForm.boostPost,
      };
      await axiosInstance.post('/business/event', payload);
      showMsg('success', t('eventCreatedSuccess'));
      setShowEventForm(false);
      setEventForm({ title: '', description: '', category: '', customCategory: '', date: '', startTime: '', endTime: '', price: '', location: '', image: null, notifyFollowers: true, boostPost: false });
      setEventErrors({});
      await fetchMyEvents();
    } catch (err) {
      const msg = extractEventError(err, t('eventCreateFailed', 'Could not create event. Please try again.'));
      showMsg('error', msg);
      setEventErrors(prev => ({ ...prev, submit: msg }));
    } finally {
      setEventSubmitting(false);
    }
  };

  const openEditForm = (event) => {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    setEditingEventId(event.id);
    const cat = event.category || '';
    const isOther = cat.startsWith('other:');
    setEventForm({
      title: event.title || '', description: event.description || '',
      category: isOther ? 'other' : cat,
      customCategory: isOther ? cat.replace('other: ', '').replace('other:', '') : '',
      date: start.toISOString().split('T')[0],
      startTime: start.toTimeString().slice(0, 5), endTime: end.toTimeString().slice(0, 5),
      price: event.price != null ? String(event.price) : '',
      location: event.location || '', image: event.image || null,
      // Track the original end date — businesses can keep or shorten it but
      // never extend. Enforced both in the UI (max= on the date input) and
      // server-side in /api/business/event/{id}.
      originalEndDate: end.toISOString(),
    });
    setEventErrors({});
    setShowEventForm(true);
  };

  // Duplicate: pre-fill the create form with text/image data from an existing
  // event but leave dates EMPTY so the user picks new ones. Status defaults
  // to draft (the user must publish via the normal create flow).
  //
  // Bug fix (Aug 2026) — the original handler only populated form state and
  // toggled `showEventForm`. When the user clicked Recreate on an expired
  // event at the bottom of the list, the composer opened at the TOP of the
  // section (off-screen) with no toast — so the click looked like "nothing
  // happens". We now:
  //   1. Wrap in try/catch and surface any failure via showMsg('error', …)
  //      so the interface never fails silently.
  //   2. Emit a success toast so the user knows a draft was created.
  //   3. Scroll the composer into view on the next tick so the newly opened
  //      form is immediately visible on any viewport height.
  const handleDuplicateEvent = (event) => {
    try {
      const cat = event.category || '';
      const isOther = cat.startsWith('other:');
      setEditingEventId(null);  // CREATE mode, not edit
      setEventForm({
        title: event.title || '', description: event.description || '',
        category: isOther ? 'other' : cat,
        customCategory: isOther ? cat.replace('other: ', '').replace('other:', '') : '',
        date: '', startTime: '', endTime: '',
        price: event.price != null ? String(event.price) : '',
        location: event.location || (business?.businessName || ''),
        image: event.image || null,
        notifyFollowers: true,
        boostPost: false,
        // Flag used by EventsSection to show the "Creating new event from
        // duplicate" banner. Cleared when the form is reset.
        duplicatedFrom: event.title || '',
      });
      setEventErrors({});
      setShowEventForm(true);
      const successMsg = t('recreateEventToast', {
        defaultValue: 'Draft created from "{{title}}" — set new dates to publish.',
        title: event.title || '',
      });
      // Two-channel feedback so the click never looks silent regardless of
      // scroll position: in-page banner (visible if the user was at the top)
      // + floating sonner toast (visible even at the bottom of the list).
      showMsg('success', successMsg, 4000);
      try { toast.success(successMsg); } catch (_) { /* sonner optional */ }
      // Scroll into view on the next paint so the form is definitely
      // visible after the composer has rendered.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="create-event-form"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } catch (err) {
      // Populating form state is synchronous and won't normally throw, but a
      // future refactor could — surface any error rather than fail silently.
      const errMsg = t('recreateEventFailedToast', 'Unable to recreate event. Please try again.');
      showMsg('error', errMsg, 4000);
      try { toast.error(errMsg); } catch (_) { /* sonner optional */ }
    }
  };

  const handleEventUpdate = async () => {
    if (!validateEventForm()) return;
    setEventSubmitting(true);
    try {
      const startDate = `${eventForm.date}T${eventForm.startTime}:00`;
      const endDate = `${eventForm.date}T${eventForm.endTime}:00`;
      const finalCategory = eventForm.category === 'other' && eventForm.customCategory.trim()
        ? `other: ${eventForm.customCategory.trim()}`
        : eventForm.category;
      const payload = {
        title: eventForm.title.trim(), description: eventForm.description.trim(),
        category: finalCategory, startDate, endDate,
        location: eventForm.location.trim(),
        price: eventForm.price ? parseFloat(eventForm.price) : null,
        image: eventForm.image || null,
      };
      await axiosInstance.put(`/business/event/${editingEventId}`, payload);
      showMsg('success', t('eventUpdatedSuccess'));
      setShowEventForm(false); setEditingEventId(null);
      setEventForm({ title: '', description: '', category: '', customCategory: '', date: '', startTime: '', endTime: '', price: '', location: '', image: null });
      setEventErrors({});
      await fetchMyEvents();
    } catch (err) {
      const msg = extractEventError(err, t('eventUpdateFailed', 'Could not update event. Please try again.'));
      showMsg('error', msg);
      setEventErrors(prev => ({ ...prev, submit: msg }));
    } finally {
      setEventSubmitting(false);
    }
  };

  const handleArchiveEvent = async (eventId) => {
    setArchiveConfirm(null);
    try {
      await axiosInstance.delete(`/business/event/${eventId}`);
      showMsg('success', t('eventArchivedSuccess'));
      await fetchMyEvents();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error archiving event');
    }
  };

  // ============= OFFERS =============
  const validateOfferForm = () => {
    const errors = {};
    if (!offerForm.title.trim()) errors.title = t('offerTitleRequired');
    if (!offerForm.description.trim()) errors.description = t('offerDescRequired');
    if (!offerForm.validUntil) errors.validUntil = t('offerValidUntilRequired');
    setOfferErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOfferSubmit = async () => {
    if (!validateOfferForm()) return;
    setOfferSubmitting(true);
    try {
      const payload = {
        title: offerForm.title.trim(), description: offerForm.description.trim(),
        discountPercentage: offerForm.discountPercentage ? parseFloat(offerForm.discountPercentage) : null,
        validUntil: `${offerForm.validUntil}T23:59:59`,
        image: offerForm.image || null,
        notifyFollowers: offerForm.notifyFollowers, boostPost: offerForm.boostPost,
      };
      await axiosInstance.post('/business/offer', payload);
      showMsg('success', t('offerCreatedSuccess'));
      setShowOfferForm(false);
      setOfferForm({ title: '', description: '', discountPercentage: '', validUntil: '', image: null, notifyFollowers: true, boostPost: false });
      setOfferErrors({});
      await fetchMyOffers();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error creating offer');
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    try {
      await axiosInstance.delete(`/business/offer/${offerId}`);
      showMsg('success', t('offerDeletedSuccess'));
      await fetchMyOffers();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error deleting offer');
    }
  };

  const openEditOffer = (offer) => {
    setEditingOfferId(offer.id);
    setOfferForm({
      title: offer.title || '',
      description: offer.description || '',
      discountPercentage: offer.discountPercentage != null ? String(offer.discountPercentage) : '',
      validUntil: offer.validUntil ? new Date(offer.validUntil).toISOString().split('T')[0] : '',
      image: offer.image || null,
      notifyFollowers: false,
      boostPost: false,
      // Track the original validUntil — businesses can shorten but not extend.
      // Enforced in the UI (max= on the date input) and server-side.
      originalValidUntil: offer.validUntil || null,
    });
    setOfferErrors({});
    setShowOfferForm(true);
  };

  // Duplicate offer: same idea as duplicate event — pre-fill text fields,
  // clear validUntil so the user must pick a new one, default status = draft.
  //
  // Bug fix (Aug 2026) — see the matching comment on `handleDuplicateEvent`
  // above. Adds try/catch + success/error toasts + scroll-into-view so the
  // Recreate button never looks silent even when the user is scrolled to the
  // bottom of the offers list.
  const handleDuplicateOffer = (offer) => {
    try {
      setEditingOfferId(null);
      setOfferForm({
        title: offer.title || '',
        description: offer.description || '',
        discountPercentage: offer.discountPercentage != null ? String(offer.discountPercentage) : '',
        validUntil: '',
        image: offer.image || null,
        notifyFollowers: true,
        boostPost: false,
        duplicatedFrom: offer.title || '',
      });
      setOfferErrors({});
      setShowOfferForm(true);
      const successMsg = t('recreateOfferToast', {
        defaultValue: 'Draft created from "{{title}}" — set a new end date to publish.',
        title: offer.title || '',
      });
      showMsg('success', successMsg, 4000);
      try { toast.success(successMsg); } catch (_) { /* sonner optional */ }
      setTimeout(() => {
        const el = document.querySelector('[data-testid="create-offer-form"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } catch (err) {
      const errMsg = t('recreateOfferFailedToast', 'Unable to recreate offer. Please try again.');
      showMsg('error', errMsg, 4000);
      try { toast.error(errMsg); } catch (_) { /* sonner optional */ }
    }
  };

  const handleOfferUpdate = async () => {
    if (!validateOfferForm()) return;
    setOfferSubmitting(true);
    try {
      const payload = {
        title: offerForm.title.trim(),
        description: offerForm.description.trim(),
        discountPercentage: offerForm.discountPercentage ? parseFloat(offerForm.discountPercentage) : null,
        validUntil: `${offerForm.validUntil}T23:59:59`,
        // Send the image field on every save — '' explicitly clears it.
        image: offerForm.image || '',
      };
      await axiosInstance.put(`/business/offer/${editingOfferId}`, payload);
      showMsg('success', t('offerUpdatedSuccess', 'Offer updated!'));
      setShowOfferForm(false);
      setEditingOfferId(null);
      setOfferForm({ title: '', description: '', discountPercentage: '', validUntil: '', image: null, notifyFollowers: true, boostPost: false });
      setOfferErrors({});
      await fetchMyOffers();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error updating offer');
    } finally {
      setOfferSubmitting(false);
    }
  };

  // ============= BOOST =============
  const handleBoostCheckout = async () => {
    if (!boostModal) return;
    setBoostLoading(true);
    try {
      const res = await axiosInstance.post('/stripe/create-boost-checkout', {
        postId: boostModal.postId, postType: boostModal.postType,
        tierId: selectedTier, originUrl: window.location.origin,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Error starting boost');
      setBoostModal(null);
    } finally {
      setBoostLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axiosInstance.post('/stripe/customer-portal', { returnUrl: window.location.href });
      window.location.href = res.data.portalUrl;
    } catch (error) {
      showMsg('error', error.response?.data?.detail || 'Failed to open billing portal', 4000);
      setPortalLoading(false);
    }
  };

  // In-app upgrade flow — does NOT use the Stripe customer portal because the
  // portal's "Update subscription" UI was leaking the legacy 6-month price.
  // Instead we cancel the existing sub at period-end and start a fresh
  // Checkout for the requested plan at current pricing.
  const handleUpgrade = async (planId = '12months') => {
    setPortalLoading(true);
    try {
      const res = await axiosInstance.post('/stripe/upgrade-checkout', {
        planId,
        originUrl: window.location.origin,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (error) {
      showMsg('error', error.response?.data?.detail || 'Failed to start upgrade', 4000);
      setPortalLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (!business) return null;
    const now = new Date();

    if (business.approvalStatus === 'pending') return { icon: 'Clock', color: 'yellow', title: t('pendingApprovalTitle'), subtitle: t('pendingApprovalDesc') };
    if (business.approvalStatus === 'rejected') return { icon: 'AlertCircle', color: 'red', title: t('applicationRejected'), subtitle: t('applicationRejectedDesc') };
    if (business.approvalStatus === 'suspended') return { icon: 'AlertCircle', color: 'red', title: t('listingSuspended'), subtitle: t('listingSuspendedDesc') };

    if (business.subscriptionStatus === 'pending') {
      return { icon: 'Clock', color: 'yellow', title: t('paymentPendingTitle', 'Payment required'), subtitle: t('paymentPendingDesc', 'Complete payment to make your listing visible. Your listing will go live as soon as payment is confirmed.') };
    }

    if (business.subscriptionStatus === 'active') {
      const subEnd = business.subscriptionEnd ? new Date(business.subscriptionEnd) : null;
      const subDaysLeft = subEnd ? Math.max(0, Math.ceil((subEnd - now) / (1000 * 60 * 60 * 24))) : null;
      return { icon: 'CheckCircle', color: subDaysLeft !== null && subDaysLeft <= 7 ? 'orange' : 'green', title: t('subscriptionActive'), subtitle: subEnd ? t('renewsOn', { date: subEnd.toLocaleDateString(), days: subDaysLeft }) : t('listingActive') };
    }

    // cancelled/expired => account once had a paid subscription that lapsed
    if (business.subscriptionStatus === 'cancelled' || business.subscriptionStatus === 'expired') {
      return { icon: 'AlertCircle', color: 'red', title: t('subscriptionExpired'), subtitle: t('subscriptionExpiredDesc') };
    }

    // Anything else (none / null / trial-leftover / unknown) — never paid yet.
    // Show an activation CTA, not an "expired" banner.
    return { icon: 'AlertCircle', color: 'orange', title: t('listingNotActiveTitle', 'Listing not active yet'), subtitle: t('listingNotActiveDesc', 'Subscribe to make your listing visible on Discover.') };
  };

  return {
    // Core
    business, loading, message, navigate, user,
    fileInputRef, posterInputRef,
    // Status
    getStatusInfo, handleLogout,
    // Subscription
    retrying, handleRetryPayment,
    // Portal
    portalLoading, handleOpenPortal, handleUpgrade,
    // Profile edit
    editing, setEditing, editForm, setEditForm, handleSaveEdit,
    uploading, handlePhotoUpload, handleDeletePhoto, handleReorderPhotos, handleLogoUpload,
    // Analytics
    analytics, analyticsPeriod, changeAnalyticsPeriod,
    // Events
    myEvents, eventsLoading, showEventForm, setShowEventForm,
    eventSubmitting, posterUploading, eventForm, setEventForm,
    eventErrors, setEventErrors, editingEventId, setEditingEventId,
    archiveConfirm, setArchiveConfirm,
    handlePosterUpload, handleEventSubmit, handleEventUpdate,
    openEditForm, handleArchiveEvent, handleDuplicateEvent, fetchMyEvents, restoreEvent,
    // Offers
    myOffers, offersLoading, showOfferForm, setShowOfferForm,
    offerSubmitting, offerForm, setOfferForm,
    offerErrors, setOfferErrors,
    editingOfferId, setEditingOfferId,
    handleOfferSubmit, handleDeleteOffer, openEditOffer, handleOfferUpdate,
    handleDuplicateOffer, restoreOffer,
    handleOfferImageUpload, offerImageUploading, offerImageInputRef,
    // Boost
    boostTiers, boostModal, setBoostModal,
    selectedTier, setSelectedTier, boostLoading,
    handleBoostCheckout,
    boostHistory, boostHistoryLoading,
    // Temporary closures / business status
    closures, closuresLoading, closureSubmitting,
    createClosure, updateClosure, deleteClosure, reopenClosure,
  };
}
